/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppDialog from "../../../../../components/AppDialog";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Paper,
  Typography,
} from "@mui/material";
import AppDialogHalfScreen from "../../../../../components/AppDialogHalfScreen";
import {
  ArrowRight,
  InfoOutlined,
  QrCode,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AppButton from "../../../../../components/AppButton";
import config from "../../../../../utils/config";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ACTIVITY_DETAIL,
  ACTIVITY_META,
  ETH,
  EVENTS,
  MESSAGE_BAR_TYPES,
  paymentOptions,
  WALLET_ADD_METHODS,
} from "../../../../../utils/constants";
import {
  checkKyt,
  checkWithdraw,
  getFees,
  logEvent,
} from "../../../../../store/api";
import { setLastRefId, showMessageBar } from "../../../../../store/actions";
import {
  Encrypt,
  extractEthereumAddress,
  findCurrency,
  formatCryptoDecimals,
  formatNumber,
  isKycVerified,
  validator,
} from "../../../../../utils";
import { withdraw as WithdrawApi } from "../../../../../store/api";
import AppToolTip from "../../../../../components/AppTooltip";
import AppLoader from "../../../../../components/AppLoader";
import ConfirmAddressOwnership from "../crypto-payment/ConfirmAddressOwnership";
import FeeComponent from "./FeeComponent";
import QrCodeScanner from "../../../../../components/QrCodeScanner";
import { openQrScanner } from "../../../../../utils/qrScanner";

export default function CryptoSummary({
  active,
  setActive,
  setShowKytBlocked,
  setShowKyt,
  amount,
  cryptoCurrency,
  flow
}) {
  const [qrcodeReader, setQrcodeReader] = useState(false);
  const [feeStructure, setFeeStructure] = useState(null);
  const account = useSelector(({ Auth }) => Auth?.account)

  const { t } = useTranslation();
  const service = useSelector(({ dashboard }) => dashboard?.fiatService);
  const user = useSelector(({ Auth }) => Auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [address, setAddress] = useState("");
  const [withdrawEnabled, setWithdrawEnabled] = useState(false);
  const [waitingText, setWaitingText] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [saveWallet, setSaveWallet] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [open, setOpen] = useState(false);
  const ownershipFormData = useRef(null);
  const [trackWalletEntry, setTrackWalletEntry] = useState(null);
  const [errors, setErrors] = useState({ address: "", amount: "" });
  const [withdrawData, setWithdrawData] = useState({
    localPayoutAmount: 0,
    localProcessingFeeFiat: 0,
    localWithoutFeePayoutAmount: 0,
  });


  // Derived state: avoids unnecessary re-renders
  const currency = useMemo(() => findCurrency(account) || "USD", [account]);

  const handleOpenFees = () => setOpen(true);

  // Reset data on active state change
  useEffect(() => {
    if (!address) {
      setErrors({ address: "", amount: "" });
      setSaveWallet(false);
      const localAddress = localStorage.getItem(
        `walletaddress-${cryptoCurrency?.currency}`
      );
      if (localAddress) {
        setAddress(localAddress);
        setSaveWallet(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoCurrency]);

  // Validate address on change
  useEffect(() => {
    if (address) {
      const timeout = setTimeout(() => validateAddress(address), 1000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const getFeesData = useCallback(async () => {
    try {
      if (!cryptoCurrency?.currency) {
        return;
      }
      const data = await getFees({
        amount,
        cryptoCurrency: cryptoCurrency?.currency,
        cryptoNetwork: cryptoCurrency?.network,
      });
      setFeeStructure(data.result);
    } catch (err) {
      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoCurrency, dispatch]);

  const handleAddressInput = async () => {
    try {
      if (!address) {
        const text = await navigator?.clipboard?.readText();
        const validate = validator(text, cryptoCurrency?.currency);
        if (validate) {
          if (text && text !== address) {
            //ignore if the same address is inputed
            logEvent({
              current: EVENTS.WITHDRAWAL.WALLET_ADD,
              activity: ACTIVITY_DETAIL.WITHDRAWAL.WALLET_ADD,
              meta: {
                data: {
                  entry: WALLET_ADD_METHODS.MANUAL,
                  pathname: location.pathname,
                  source: ACTIVITY_META.WITHDRAWAL.WALLET_ADD,
                },
              },
            });
          }
          handleAddressChange(text);
        }
      }
    } catch (err) {
      // console.log(err);
    }
  };


  useEffect(() => {
    getFeesData();
  }, [cryptoCurrency, getFeesData]);

  const handleManualAddressChange = useCallback((event) => {
    const val = event.target.value;
    if (trackWalletEntry) clearTimeout(trackWalletEntry);

    if (val && address !== val) {
      const walletEntry = setTimeout(() => {
        logEvent({
          current: EVENTS.WITHDRAWAL.WALLET_ADD,
          activity: ACTIVITY_DETAIL.WITHDRAWAL.WALLET_ADD,
          meta: {
            data: {
              entry: WALLET_ADD_METHODS.MANUAL,
              pathname: location.pathname,
              source: ACTIVITY_META.WITHDRAWAL.WALLET_ADD,
            },
          },
        });
      }, 1000);
      setTrackWalletEntry(walletEntry);
    }

    handleAddressChange(val);
  }, [address, location.pathname, trackWalletEntry]);

  const handleAddressChange = (val) => {
    setAddress(val);
    setErrors({ address: "" });
    setWithdrawEnabled(false);
  };

  const setQrData = (text) => {
    if (cryptoCurrency?.currency === ETH) {
      text = extractEthereumAddress(text);
    }

    if (!validator(text, cryptoCurrency?.currency, cryptoCurrency?.network)) {
      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, t("invalidAddress")));
      return;
    }

    logEvent({
      current: EVENTS.WITHDRAWAL.WALLET_ADD,
      activity: ACTIVITY_DETAIL.WITHDRAWAL.WALLET_ADD,
      meta: {
        data: {
          entry: WALLET_ADD_METHODS.CAMERA_SCAN,
          pathname: location.pathname,
          source: ACTIVITY_META.WITHDRAWAL.WALLET_ADD,
        },
      },
    });

    handleAddressChange(text);
    setQrcodeReader(false);
  };
  const updateOwnershipFormData = (data) => {
    ownershipFormData.current = data;
  }
  const validateWithdraw = () => {
    // eslint-disable-next-line eqeqeq
    if (user && user.kycStatus && user.kycStatus == 3) {
      setShowKytBlocked(true);
      return false;
    }
    // eslint-disable-next-line eqeqeq
    if (user && user.kycStatus && !isKycVerified(user)) {
      setShowKyt(true);
      return false;
    }

    return true;
  };

  const validateAddress = async (address, flag = false) => {
    if (!address) return;

    setErrors({ address: "" });
    setWaitingText(t("CheckingWallet"));

    if (!validator(address, cryptoCurrency?.currency, cryptoCurrency?.network)) {
      setErrors({ address: t("invalidAddress") });
      setWithdrawEnabled(false);
      setWaitingText("");
      return false;
    }

    try {
      await checkKyt({ walletAddress: address, currency: cryptoCurrency?.currency });

      const payload = {
        encryptedReq: Encrypt(JSON.stringify({
          amount,
          currency,
          cryptoCurrency: cryptoCurrency?.currency,
          walletAddress: address,
          cryptoNetwork: cryptoCurrency?.network,
        })),
      };

      if (!flag) {
        const data = await checkWithdraw(payload);
        setWithdrawData(data.result);
      }

      setWithdrawEnabled(true);
      setWaitingText("");
      return true;
    } catch (err) {
      setWithdrawEnabled(false);
      setErrors({ address: err?.response?.data?.message || err.message });
      setWaitingText("");
      return false;
    }
  };

  const handleTransferChange = () => {
    setConfirm(false);
  };

  const withdraw = async () => {
    if (!validateWithdraw()) {
      return;
    }
    if (!address || !(await validateAddress(address, true))) return;

    setWithdrawLoading(true);
    setWaitingText("Initiating Transfer Request...");

    const payload = {
      encryptedReq: Encrypt(JSON.stringify({
        amount,
        currency,
        cryptoCurrency: cryptoCurrency?.currency,
        walletAddress: address,
        cryptoNetwork: cryptoCurrency?.network,
        recordId: withdrawData?.recordId,
        ownershipInfo: ownershipFormData.current,
      })),
    };

    try {
      const data = await WithdrawApi(payload);

      if (data?.result?.error) {
        if (data?.result?.isTravelRuleReject) {
          navigate(`/withdraw/status/undefined?isTravelRuleReject=1`);
        } else {
          dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, data?.result?.message));
        }
        return;
      }

      dispatch(setLastRefId(data.result.id));

      if (saveWallet) {
        localStorage.setItem(`walletaddress-${cryptoCurrency?.currency}`, address);
      } else {
        localStorage.removeItem(`walletaddress-${cryptoCurrency?.currency}`);
      }

      navigate(`/withdraw/status/${data.result.id}`);
    } catch (err) {
      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err));
    } finally {
      setWithdrawLoading(false);
      setWaitingText("");
    }
  };

  const isDisabled = () => {
    return withdrawLoading || !address || !withdrawEnabled;
  };

  const handleSaveWallet = (ev) => {
    logEvent({
      current: EVENTS.WITHDRAWAL.REMEMBER_WALLET,
      activity: ACTIVITY_DETAIL.WITHDRAWAL.REMEMBER_WALLET,
      meta: {
        data: {
          remember: ev.target.checked,
          pathname: location.pathname,
          source: ACTIVITY_META.WITHDRAWAL.REMEMBER_WALLET,
        },
      },
    });
    setSaveWallet(ev.target.checked);
  };


  const confirmAction = () => {
    if (!service?.travelRule) {
      setConfirm(true);
    } else {
      logEvent({
        current: EVENTS.WITHDRAWAL.CRYPTO_SELECTION,
        activity: ACTIVITY_DETAIL.WITHDRAWAL.CRYPTO_SELECTION,
        meta: { data: { address, pathname: location.pathname, source: ACTIVITY_META.WITHDRAWAL.CONFIRM_ADDRESS } },
      });
      setActive(active + 1);
    }
  };
  if ((flow === paymentOptions.crypto2 && active === 3) ||
    (flow === paymentOptions.crypto3 && active === 4)) {
    return <Box>
      <ConfirmAddressOwnership
      address={address}
      network={cryptoCurrency?.network}
      cryptoCurrency={cryptoCurrency?.currency}
      handleConfirm={() => setConfirm(true)}
      handleTransferChange={handleTransferChange}
      updateOwnershipFormData={updateOwnershipFormData} />
           <AppDialogHalfScreen
        show={confirm}
        setShow={val => setConfirm(val)}
        title={t("confirmWalletAddress")}
        subtitle={t("confirmWalletAddressSub")}
        note={t("confirmWalletAddressNote")}
        component={
          <Box width={"100%"}>
            <Typography fontSize={14}>{t("walletaddress")}</Typography>
            <Typography
              padding={1}
              border="1px solid #F8F8F8"
              borderRadius={1}
              fontSize={14}
              sx={{ overflowWrap: "anywhere" }}
            >
              {address}
            </Typography>
            <Box
              display={"flex"}
              flexDirection={"row"}
              marginTop={4}
              justifyContent="space-between"
              width={"100%"}
            >
              <Typography fontSize={14} fontWeight={700} fontFamily={"Manrope"}>
                {t("Coin")}: {cryptoCurrency?.currency}
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={700}
                fontFamily={"Manrope"}
                textTransform={"capitalize"}
              >
                {t("network")}: {cryptoCurrency?.network}
              </Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              marginTop={1}
              justifyContent="space-between"
              width={"100%"}
            >
              <AppButton
                title={t("Change")}
                buttontheme={"light"}
                onClick={() => {setConfirm(false); setActive(active - 1)}}
                sx={{ width: 100, marginLeft: 5 }}
              ></AppButton>
              <AppButton
                title={t("Confirm")}
                buttontheme={"success"}
                onClick={() => {
                  setConfirm(false);
                  withdraw();
                }}
                sx={{ maxWidth: 150, marginRight: 5 }}
              ></AppButton>
            </Box>
          </Box>
        }
      /></Box>
  }
  return (
    <Paper
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        // minHeight: [1, 2].includes(active) ? "75vh" : "24vh",
      }}
    >
      <AppDialog
        show={qrcodeReader}
        onChangeShow={setQrcodeReader}
        fullScreen
        bgColor={"#000000"}
      >
        <QrCodeScanner setQrData={setQrData} />
      </AppDialog>
      <AppDialogHalfScreen
        show={confirm}
        setShow={val => setConfirm(val)}
        title={t("confirmWalletAddress")}
        subtitle={t("confirmWalletAddressSub")}
        note={t("confirmWalletAddressNote")}
        component={
          <Box width={"100%"}>
            <Typography fontSize={14}>{t("walletaddress")}</Typography>
            <Typography
              padding={1}
              border="1px solid #F8F8F8"
              borderRadius={1}
              fontSize={14}
              sx={{ overflowWrap: "anywhere" }}
            >
              {address}
            </Typography>
            <Box
              display={"flex"}
              flexDirection={"row"}
              marginTop={4}
              justifyContent="space-between"
              width={"100%"}
            >
              <Typography fontSize={14} fontWeight={700} fontFamily={"Manrope"}>
                {t("Coin")}: {cryptoCurrency?.currency}
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={700}
                fontFamily={"Manrope"}
                textTransform={"capitalize"}
              >
                {t("network")}: {cryptoCurrency?.network}
              </Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              marginTop={1}
              justifyContent="space-between"
              width={"100%"}
            >
              <AppButton
                title={t("Change")}
                buttontheme={"light"}
                onClick={() => setConfirm(false)}
                sx={{ width: 100, marginLeft: 5 }}
              ></AppButton>
              <AppButton
                title={t("Confirm")}
                buttontheme={"success"}
                onClick={() => {
                  setConfirm(false);
                  withdraw();
                }}
                sx={{ maxWidth: 150, marginRight: 5 }}
              ></AppButton>
            </Box>
          </Box>
        }
      />
      <Typography
        color={"#F8F8F8"}
        fontSize={16}
        textAlign="center"
        paddingTop={1}
        paddingBottom={1}
      >
        {t("add.wallet")}
      </Typography>
      {/* <FormControl variant="filled" fullWidth > */}
      <OutlinedInput
        fullWidth
        type="text"
        sx={{
          fontSize: 18,
          padding: "10px 10px",
          marginTop: 1,
          ".MuiOutlinedInput-notchedOutline": {
            display: "none",
          },
        }}
        autoComplete={"off"}
        value={address}
        onChange={handleManualAddressChange}
        placeholder={t("recurring.enterwalletaddress")}
        onFocus={(ev) => handleAddressInput(ev)}
        error={!!errors.address}
        endAdornment={
          <InputAdornment position="end">
            <AppToolTip title={t("ScanQR")} arrow enterTouchDelay={0}>
              <IconButton
                edge="end"
                onClick={() =>
                  openQrScanner({
                    onScanned: setQrData,
                    onUseWeb: () => setQrcodeReader(true),
                    onError: (msg) =>
                      dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, msg)),
                  })
                }
                aria-label="tracker"
                aria-describedby="qr_code_scanner"
              >
                <QrCode
                  sx={{ color: "#F8FAFF", width: 40, height: 40 }}
                  aria-label="tracker"
                  aria-describedby="qr_code_scanner"
                />
              </IconButton>
            </AppToolTip>
          </InputAdornment>
        }
        label="Address"
      />
      <Typography
        fontSize={14}
        fontWeight={700}
        paddingTop={1}
        paddingBottom={1}
        textTransform="capitalize"
        color={"#DC2626"}
        textAlign={"left"}
      >
        {!!errors.address && errors.address}
      </Typography>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Typography fontSize={13} color="#F8F8F8" fontWeight={700}>
          <Checkbox
            checked={saveWallet}
            onChange={handleSaveWallet}
            name="save wallet"
          />{" "}
          {t("rememberWallet")}
        </Typography>
        <Link
          href={config.ONBOARDING_GUIDE_LINK + "/how-to-create-wallet"}
          target="_blank"
          fontFamily={"Manrope"}
          fontSize={13}
        >
          {t("howtocreatewallet")}
        </Link>
      </Box>
      <br />
      <Box>
        {withdrawData && (
          <Box
            sx={{
              color: "#F8FAFF",
            }}
          >
            <Divider />
            <Grid container sx={{ color: "#D6E3F0", margin: 0 }}>
              <Grid item xs={6}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  fontWeight={400}
                  textAlign="left"
                  textTransform="capitalize"
                  color="#F8F8F8"
                >
                  {t("Amount Requested")}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  fontWeight={400}
                  textAlign="right"
                  textTransform="capitalize"
                  color="#F8F8F8"
                >
                  {` ${formatNumber(amount)} ${currency}`}
                </Typography>
              </Grid>
              <Grid item xs={6} display={"flex"} alignItems={"center"}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  textAlign="left"
                  fontWeight={400}
                  textTransform="capitalize"
                  color="#F8F8F8"
                >
                  {t("totalFees")}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                >
                  <Typography
                    fontFamily={"Manrope"}
                    fontSize={14}
                    textAlign="right"
                    textTransform="uppercase"
                    fontWeight={400}
                    color="#F8F8F8"
                  >
                    {formatNumber(feeStructure?.totalFeeUsd)} {findCurrency(account)}
                  </Typography>
                  {feeStructure?.totalFeeUsd && <IconButton
                    onClick={() => handleOpenFees()}
                    sx={{ padding: 0, marginTop: 0.2 }}
                  >
                    <ArrowRight sx={{ color: "#F8F8F8" }} />
                  </IconButton>}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  textAlign="left"
                  textTransform="capitalize"
                  fontWeight={400}
                  color="#F8F8F8"
                >
                  {t("exchangeRate")}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  textAlign="right"
                  textTransform="uppercase"
                  fontWeight={400}
                  color="#F8F8F8"
                >
                  1 {cryptoCurrency?.currency} ={" "}
                  {formatNumber(withdrawData?.cryptoCurrencyCurrentRate || feeStructure?.cryptoCurrencyCurrentRate)}{" "}
                  {currency}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography
                  fontFamily={"Manrope"}
                  fontSize={14}
                  textAlign="left"
                  textTransform="capitalize"
                  fontWeight={400}
                  color="#F8F8F8"
                >
                  {t("AmountReceived")}:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                >
                  <Typography
                    fontFamily={"Manrope"}
                    fontSize={14}
                    textAlign="right"
                    textTransform="uppercase"
                    fontWeight={400}
                    color="#F8F8F8"
                  >
                    {withdrawData?.payoutAmountCrypto
                      ? `${formatCryptoDecimals(
                        withdrawData.payoutAmountCrypto
                      )} ${cryptoCurrency?.currency}`
                      : "---"}
                  </Typography>
                  {withdrawData?.payoutAmountCrypto && (
                    <AppToolTip
                      title={t("approxDisclaimer")}
                      arrow
                      enterTouchDelay={0}
                    >
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        marginLeft={1}
                      >
                        <InfoOutlined />
                      </Box>
                    </AppToolTip>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {waitingText && (
        <Box
          display={"flex"}
          flexDirection="row"
          alignItems={"center"}
          marginTop={2}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <AppLoader
            title={waitingText}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            fontSize={16}
          />
        </Box>
      )}

      <Button
        variant="contained"
        size="large"
        color="primary"
        disabled={isDisabled()}
        sx={{
          color: "#041939",
          marginTop: 2,
          fontSize: 16,
          width: "100%",
          textTransform: "capitalize",
          borderRadius: "8px",
          ":disabled": { backgroundColor: "#0088F5", opacity: 0.4 },
        }}
        onClick={confirmAction}
      >
        {service?.travelRule ? t("loginPage.continue") : t("transfer")}
      </Button>

      <Box marginTop={1}>
        <Typography
          variant="subtitle1"
          color={"#F8F8F8"}
          textAlign={"left"}
          fontSize={12}
          lineHeight={1.5}
        >
          <ol style={{ paddingLeft: 12 }}>
            <li>{t("transfer.disclaimer1")}</li>
            <li>{t("transfer.disclaimer2")}</li>
          </ol>
        </Typography>
      </Box>

      <AppDialogHalfScreen show={open} onChangeShow={setOpen}>
        <FeeComponent feeStructure={feeStructure} withdrawData={withdrawData} setOpen={setOpen} />
      </AppDialogHalfScreen>
    </Paper>
  );
}
